import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Building2, Users, MapPin, UserPlus, Activity, Crown } from 'lucide-react';
import {
  useAdminData, Metric, LoadingBlock, ErrorBox, ExcludedNote, count,
} from '../../components/admin/AdminKit';

// WO-36 — customer companies only. "By industry" (never asked at signup, so
// always "Unknown") and "Trial" (no such status exists) are gone; pending
// invitations are now counted from the real invitations.

const FLAGS = { SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦' };

export default function AdminAccounts() {
  const { data, loading, error, reload } = useAdminData('/api/analytics/accounts/overview');
  if (loading) return <LoadingBlock testid="admin-accounts-loading" />;
  const h = data?.account_health || {};

  return (
    <div className="space-y-6" data-testid="admin-accounts-page">
      <div>
        <h1 className="text-2xl font-bold">Accounts</h1>
        <p className="text-muted-foreground">Customer companies and the people in them</p>
      </div>
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <>
          <ExcludedNote excluded={data.excluded} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Companies" icon={Building2} value={count(data.total_companies)} />
            <Metric label="People" icon={Users} value={count(data.total_users)} />
            <Metric label="Companies active (30 days)" icon={Activity} value={count(h.active_30d)}
              note="Someone in the company used the app" />
            <Metric label="Pending Team invitations" icon={UserPlus} value={count(data.pending_invitations)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" />Companies by country</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(data.by_country || {}).sort((a, b) => b[1] - a[1]).map(([country, n]) => (
                  <div key={country} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{FLAGS[country] || '🌍'}</span>
                      <span>{country}</span>
                    </div>
                    <Badge variant="secondary">{n}</Badge>
                  </div>
                ))}
                {Object.keys(data.by_country || {}).length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No customer company yet</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-primary" />Largest companies</CardTitle>
                <CardDescription>By number of people · badge = the highest access level anyone in it has</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.users_per_company || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.company}</p>
                      <Badge variant="outline" className="capitalize text-xs">{c.plan}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="font-bold">{c.user_count}</span>
                    </div>
                  </div>
                ))}
                {(data.users_per_company || []).length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No customer company yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Metric label="Companies active" value={count(h.active)} />
            <Metric label="Companies suspended" value={count(h.suspended)} />
            <Metric label="Industry of each company" value={null} why="The app does not ask for it at signup" />
          </div>
        </>
      )}
    </div>
  );
}
